import * as angular from "angular";
import * as d3 from "d3";
import { DialogUtils } from "../../../helper/dialog.helper";
import { AdminActivityService } from "../../../services/admin-activity.service";
import { AdminEnterService } from "../../../services/admin-enter.service";
import { ToastUtils } from "../../../helper/toast.helper";
import * as activity from "../lists/admin-activity.helper";

interface Bucket {
    bucketStart: string;
    interactions: number;
    interactionsDelta: number | null;
    logins: number;
    activeUsers: number | null;
    contactsAdded: number;
}

interface BreakdownRow {
    campaignid: number;
    name: string;
    region: string;
    interactions: number;
    interactionsDelta: number | null;
    logins: number;
    activeUsers: number;
    trend: string | null;
    share: number;
}

interface HistoryPayload {
    kind: "campaign" | "organisation";
    record: any;
    summary: activity.ActivitySummary;
    bucket: "day" | "week";
    buckets: Bucket[];
    breakdown?: BreakdownRow[];
}

/**
 * AV-17958: activity history for one account or one organisation. Opened from
 * the Trend pill or the sparkline. An organisation dialog lists its member
 * accounts, and each of those opens the account's own history in place, with
 * a back control, so the path organisation -> account -> inside the campaign
 * never returns to the table.
 */
let AdminActivityDialogComponent = {
    selector: "vcAdminActivityDialog",
    templateUrl:
        "/angular/components/admin/dialogs/admin-activity-dialog.component.html",
    bindings: {
        kind: "<",
        recordId: "<",
        range: "<",
    },
    controller: class AdminActivityDialogController {
        // * bindings for typescript compiler *
        public kind: "campaign" | "organisation";
        public recordId: number | string;
        public range: number;
        // ************************************

        public loading = true;
        public error: string | null = null;
        public data: HistoryPayload | null = null;
        public bucket: "day" | "week" = "day";
        public entering = false;
        /** Organisation dialog the current account view was reached from. */
        public parent: {
            data: HistoryPayload;
            bucket: "day" | "week";
            scrollTop: number;
        } | null = null;

        public deltaText = activity.deltaText;
        public deltaClass = activity.deltaClass;
        public relativeDays = activity.relativeDays;
        public shortDate = activity.shortDate;
        public trendKey = activity.trendKey;
        public trendLabel = activity.trendLabel;
        public trendIcon = activity.trendIcon;
        public bucketLabel = activity.bucketLabel;
        public rangeLabel = activity.rangeLabel;

        private dialog: DialogUtils;
        private service: AdminActivityService;
        private enter: AdminEnterService;
        private toast: ToastUtils;
        private $element: any;
        private $timeout: any;

        // Parameter names are the injector's lookup keys (implicit annotation).
        constructor(
            DialogUtils: DialogUtils,
            AdminActivityService: AdminActivityService,
            AdminEnterService: AdminEnterService,
            ToastUtils: ToastUtils,
            $element: any,
            $timeout: any
        ) {
            this.dialog = DialogUtils;
            this.service = AdminActivityService;
            this.enter = AdminEnterService;
            this.toast = ToastUtils;
            this.$element = $element;
            this.$timeout = $timeout;
        }

        $onInit(): void {
            // Sweep any tooltip stranded by an earlier open. nvd3 only hides
            // its guideline tooltip on mouse-out, so closing the dialog with
            // the pointer still over the chart leaves one behind, and they
            // stack up one per open.
            this.clearChartTooltips();
            this.range = Number(this.range) || 7;
            // 90 days reads best by week; everything else by day.
            this.bucket = this.range === 90 ? "week" : "day";
            this.load();
        }

        // ---------- loading ----------

        private load(): void {
            this.loading = true;
            this.error = null;
            const request =
                this.kind === "organisation"
                    ? this.service.organisation(
                          this.recordId,
                          this.range,
                          this.bucket
                      )
                    : this.service.campaign(
                          this.recordId,
                          this.range,
                          this.bucket
                      );
            request.then(
                (response: any): void => {
                    const json = response.data;
                    if (json && json.success && json.data) {
                        this.data = json.data;
                        this.selectedIndex = null;
                        this.buildChart();
                        this.fitChart();
                    } else {
                        this.error =
                            (json && json.message) || "Could not load activity";
                    }
                    this.loading = false;
                },
                (response: any): void => {
                    this.loading = false;
                    this.error =
                        (response && response.data && response.data.message) ||
                        "Could not load activity";
                }
            );
        }

        setBucket(bucket: "day" | "week"): void {
            if (this.bucket === bucket) return;
            this.bucket = bucket;
            this.load();
        }

        /** Days / Weeks only makes sense once there are weeks to show. */
        canRebucket(): boolean {
            return this.range >= 30;
        }

        eyebrow(): string {
            if (this.kind === "organisation") return "Organisation activity";
            return "Activity";
        }

        rangeWords(): string {
            return activity.rangeWords(this.range);
        }

        bucketHeading(): string {
            return this.bucket === "week" ? "Week starting" : "Day";
        }

        bucketDeltaLabel(): string {
            return this.bucket === "week" ? "vs prev wk" : "vs prev day";
        }

        // ---------- chart (shared nvd3 stack) ----------

        public chartData: any[] = [];
        public chartOptions: any = null;
        /** angular-nvd3 api; refresh() re-measures the container and redraws. */
        public chartApi: any = {};
        /** Bucket pinned by clicking the chart, newest-first index into data.buckets. */
        public selectedIndex: number | null = null;

        private buildChart(): void {
            // New options mean a new nvd3 chart instance, and the old one's
            // tooltip stays in the body if the pointer was over the chart.
            this.clearChartTooltips();
            const data = this.data;
            if (!data) {
                this.chartData = [];
                return;
            }
            const rows = this.series();
            const bucket = this.bucket;
            const labelFor = (i: number): string => {
                const b = rows[Math.round(i)];
                return b ? activity.bucketLabel(b.bucketStart, bucket) : "";
            };
            // chartSeries() reverses to oldest-first itself; hand it the stored order
            this.chartData = activity.chartSeries(data.buckets, {
                interactions: "#00855A",
                logins: "#3477D1",
                average: "#9FA1A2",
            });
            const maxInt = Math.max(1, ...rows.map((b) => b.interactions || 0));
            const maxLog = Math.max(1, ...rows.map((b) => b.logins || 0));
            this.chartOptions = {
                chart: {
                    type: "multiChart",
                    height: 260,
                    margin: { top: 16, right: 56, bottom: 40, left: 56 },
                    showLegend: false,
                    useInteractiveGuideline: true,
                    duration: 0,
                    noData: "No snapshots in this window yet.",
                    xAxis: {
                        tickFormat: labelFor,
                        tickPadding: 10,
                        // one tick per bucket, thinned when there are many
                        tickValues: rows
                            .map((b, i) => i)
                            .filter(
                                (i) =>
                                    i %
                                        Math.max(
                                            1,
                                            Math.ceil(rows.length / 8)
                                        ) ===
                                    0
                            ),
                    },
                    // showMaxMin off: nvd3 would print the padded domain top
                    // ("421") over the nearest round tick.
                    yAxis1: {
                        tickFormat: d3.format(",d"),
                        tickPadding: 8,
                        showMaxMin: false,
                    },
                    yAxis2: {
                        tickFormat: d3.format(",d"),
                        tickPadding: 8,
                        showMaxMin: false,
                    },
                    yDomain1: [0, Math.ceil(maxInt * 1.1)],
                    yDomain2: [0, Math.ceil(maxLog * 1.1)],
                    interactiveLayer: {
                        tooltip: {
                            headerFormatter: labelFor,
                            valueFormatter: (v: number): string =>
                                d3.format(",d")(v),
                        },
                    },
                },
            };
        }

        /**
         * nvd3 sizes the svg from the container it finds at draw time. The
         * dialog is still animating open then, so on wide screens the chart
         * comes out narrower than the card. Redraw once the dialog has
         * settled, and again whenever the window changes.
         */
        /**
         * nvd3 appends its tooltip to document.body rather than to the chart,
         * so closing the dialog leaves the last one it drew floating over the
         * page with nothing to dismiss it. Every redraw (new options, the
         * fit-to-width refresh, Days/Weeks) makes a fresh chart with a fresh
         * tooltip, so the same sweep runs before each of those too.
         *
         * The admin app draws no other nvd3 chart, so clearing the class
         * outright is safe here; the guard keeps it harmless if that ever
         * changes and one is still on screen.
         */
        $onDestroy(): void {
            this.clearChartTooltips();
        }

        private clearChartTooltips(): void {
            const tips = document.querySelectorAll(".nvtooltip");
            for (let i = 0; i < tips.length; i++) {
                const node = tips[i];
                if (node.parentNode) {
                    node.parentNode.removeChild(node);
                }
            }
        }

        private fitChart(): void {
            const refresh = (): void => {
                if (this.chartApi && this.chartApi.refresh) {
                    this.clearChartTooltips();
                    this.chartApi.refresh();
                }
            };
            this.$timeout(refresh, 350);
            this.$timeout(refresh, 900);
        }

        /** on-ready from the nvd3 directive: a click on the chart pins that bucket. */
        chartReady = (scope: any): void => {
            const chart = scope && scope.chart;
            if (!chart || !chart.interactiveLayer) return;
            chart.interactiveLayer.dispatch.on(
                "elementClick",
                (e: any): void => {
                    const rows = this.series();
                    const i = Math.max(
                        0,
                        Math.min(rows.length - 1, Math.round(e.pointXValue))
                    );
                    this.$timeout((): void => {
                        // buckets are stored newest first
                        this.selectedIndex = rows.length
                            ? rows.length - 1 - i
                            : null;
                    });
                }
            );
        };

        selected(): Bucket | null {
            if (!this.data || this.selectedIndex === null) return null;
            return this.data.buckets[this.selectedIndex] || null;
        }

        clearSelected(): void {
            this.selectedIndex = null;
        }

        /** Oldest first for drawing; buckets arrive newest first. */
        private series(): Bucket[] {
            return this.data ? this.data.buckets.slice().reverse() : [];
        }

        averageValue(): number {
            const rows = this.series();
            if (!rows.length) return 0;
            return Math.round(
                rows.reduce((s, b) => s + (b.interactions || 0), 0) /
                    rows.length
            );
        }

        // ---------- organisation breakdown ----------

        shareStyle(row: BreakdownRow): { width: string } {
            return { width: Math.max(2, row.share || 0) + "%" };
        }

        shareClass(row: BreakdownRow): string {
            if (row.trend === "dropped") return "ec-share-dropped";
            if (row.trend === "quiet") return "ec-share-quiet";
            return "";
        }

        /** Open a member account's own history in place, keeping range and bucket. */
        openAccount(row: BreakdownRow): void {
            if (!this.data) return;
            const body = this.$element[0].querySelector(".ec-act-body");
            this.parent = {
                data: this.data,
                bucket: this.bucket,
                scrollTop: body ? body.scrollTop : 0,
            };
            this.kind = "campaign";
            this.recordId = row.campaignid;
            this.data = null;
            this.load();
        }

        back(): void {
            if (!this.parent) return;
            const parent = this.parent;
            this.parent = null;
            this.kind = "organisation";
            this.recordId = parent.data.record.oid;
            this.bucket = parent.bucket;
            this.data = parent.data;
            this.selectedIndex = null;
            this.buildChart();
            this.fitChart();
            this.loading = false;
            this.$timeout((): void => {
                const body = this.$element[0].querySelector(".ec-act-body");
                if (body) body.scrollTop = parent.scrollTop;
            });
        }

        // ---------- entering ----------

        enterLabel(): string {
            return this.kind === "organisation"
                ? "Enter organisation"
                : "Enter account";
        }

        enterCurrent(): void {
            if (!this.data || this.entering) return;
            this.entering = true;
            const record = this.data.record;
            const promise =
                this.kind === "organisation"
                    ? this.enter.enterOrganisation({
                          oid: record.oid,
                          first_campaignid: record.first_campaignid,
                      })
                    : this.enter.enterAccount({
                          campaignid: record.campaignid,
                      });
            promise.catch((message: any): void => {
                this.entering = false;
                this.toast.error(
                    typeof message === "string"
                        ? message
                        : "Could not open this record"
                );
            });
        }

        enterAccount(row: BreakdownRow): void {
            if (this.entering) return;
            this.entering = true;
            this.enter
                .enterAccount({ campaignid: row.campaignid })
                .catch((message: any): void => {
                    this.entering = false;
                    this.toast.error(
                        typeof message === "string"
                            ? message
                            : "Could not open this account"
                    );
                });
        }

        close(): void {
            // Clear on the way out as well as on destroy: the dialog's
            // closing animation would otherwise leave the tooltip visible
            // for the length of the fade.
            this.clearChartTooltips();
            this.dialog.cancel();
        }
    },
};

angular
    .module("admin")
    .component(
        AdminActivityDialogComponent.selector,
        AdminActivityDialogComponent
    );
